import {
  Dossier,
  DossierComments,
  DossierCommentsEnum,
  DossierCommentsType,
  DossierState,
  DossierStateHistory,
  UserType,
} from '../models';

const COMMENT_TYPES = new Set<DossierCommentsType>(
  Object.values(DossierCommentsEnum) as DossierCommentsType[]
);

const UNKNOWN_LEGACY_USERNAME = 'Unknown user';

type LegacyCommentConfig = {
  sourceField:
    | 'appointment.comment'
    | 'requestAppointment.comment'
    | 'claim.sinisterComment'
    | 'insurerData.comment'
    | 'insurerData.abandonmentComment'
    | 'insurerData.finishLaterComment';
  commentType: DossierCommentsType;
  preferredStates?: DossierState[];
  value?: string;
};

/** Returns a stable ISO timestamp, preferring dossier update metadata. */
function getFallbackTimestamp(dossier?: Partial<Dossier>): string {
  const date = dossier?.updateDate || dossier?.createDate || new Date();
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? new Date().toISOString()
    : parsedDate.toISOString();
}

/** Converts supported date values to ISO without letting malformed legacy data fail migration. */
function normalizeTimestamp(
  value: string | Date | undefined,
  fallback: string
): string {
  if (!value) {
    return fallback;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime())
    ? fallback
    : parsedDate.toISOString();
}

/** Builds the best available legacy insurer author name. */
function getInsurerAgentName(dossier?: Partial<Dossier>): string | undefined {
  const first = dossier?.insurerData?.callAgentFirstname?.trim();
  const last = dossier?.insurerData?.callAgentLastname?.trim();
  const fullName = [first, last].filter(Boolean).join(' ').trim();
  return fullName || undefined;
}

/** Finds the latest named actor for the first matching lifecycle state. */
function getStateHistoryEntry(
  dossier: Partial<Dossier> | undefined,
  preferredStates: DossierState[] = []
): DossierStateHistory | undefined {
  const attributedHistory = (dossier?.stateHistory || []).filter(
    (entry) => !!entry?.userName?.trim() || !!entry?.userType?.trim()
  );

  for (const state of preferredStates) {
    const matchingEntries = attributedHistory.filter(
      (entry) => entry.state === state
    );
    if (matchingEntries.length) {
      return matchingEntries[matchingEntries.length - 1];
    }
  }

  return null;
}

/** Resolves legacy author metadata from dossier lifecycle before weaker fallbacks. */
function getLegacyCommentAttribution(
  dossier: Partial<Dossier> | undefined,
  preferredStates: DossierState[] = []
): Pick<DossierComments, 'userName' | 'userType' | 'timestamp'> {
  const fallbackTimestamp = getFallbackTimestamp(dossier);
  const historyEntry = getStateHistoryEntry(dossier, preferredStates);

  if (historyEntry) {
    return {
      userName:
        historyEntry.userName?.trim() ||
        historyEntry.userId?.trim() ||
        UNKNOWN_LEGACY_USERNAME,
      userType: historyEntry.userType,
      timestamp: normalizeTimestamp(historyEntry.timestamp, fallbackTimestamp),
    };
  }

  const insurerAgentName = getInsurerAgentName(dossier);
  return {
    userName:
      insurerAgentName ||
      dossier?.currentlyInUse?.userName?.trim() ||
      UNKNOWN_LEGACY_USERNAME,
    userType: insurerAgentName ? 'INSURER' : undefined,
    timestamp: fallbackTimestamp,
  };
}

/** Converts one populated legacy field into the shared comment structure. */
function buildLegacyComment(
  dossier: Partial<Dossier> | undefined,
  config: LegacyCommentConfig
): DossierComments | null {
  const comment = config.value?.trim();
  if (!comment) {
    return null;
  }

  const attribution = getLegacyCommentAttribution(
    dossier,
    config.preferredStates
  );

  return {
    ...attribution,
    comment,
    commentType: config.commentType,
    isPinned: false,
  };
}

/** Restricts persisted comment types to values supported by the model. */
function isValidCommentType(value: unknown): value is DossierCommentsType {
  return (
    typeof value === 'string' && COMMENT_TYPES.has(value as DossierCommentsType)
  );
}

/** Cleans one collection entry while preserving its original author metadata. */
function sanitizeComment(
  comment: Partial<DossierComments>,
  dossier?: Partial<Dossier>,
  user?: { id: string; type: UserType; name?: string }
): DossierComments | null {
  const text = comment?.comment?.trim();
  if (!text) {
    return null;
  }

  const timestamp = normalizeTimestamp(
    comment?.timestamp,
    getFallbackTimestamp(dossier)
  );

  return {
    userName:
      comment?.userName?.trim() ||
      user?.name ||
      getLegacyCommentAttribution(dossier).userName,
    comment: text,
    userType:
      comment?.userType ||
      user?.type ||
      getLegacyCommentAttribution(dossier)?.userType,
    timestamp,
    commentType: isValidCommentType(comment?.commentType)
      ? comment.commentType
      : DossierCommentsEnum.other,
    isPinned: !!comment?.isPinned,
  };
}

/** Identifies an equivalent migrated comment independently from its inferred timestamp. */
function getDedupKey(comment: DossierComments): string {
  return `${
    comment.commentType
  }|${comment.comment.toLowerCase()}|${comment.userName.toLowerCase()}`;
}

/** Identifies the same persisted timeline event copied by repeated saves. */
function getTimelineEventDedupKey(comment: DossierComments): string {
  return JSON.stringify([
    comment.commentType,
    comment.comment,
    comment.userName,
    comment.userType,
    comment.timestamp,
    comment.isPinned,
  ]);
}

/** Reads every supported legacy field without mutating the source dossier. */
export function extractLegacyDossierComments(
  dossier?: Partial<Dossier>
): DossierComments[] {
  const legacyDossier = dossier as any;
  const legacyConfigs: LegacyCommentConfig[] = [
    {
      sourceField: 'appointment.comment',
      commentType: DossierCommentsEnum.appointment,
      preferredStates: [
        'COMPLETE',
        'SLOT_SUGGESTED',
        'CANCELLED',
        'NO_SHOW',
        'DONE',
      ],
      value: legacyDossier?.appointment?.comment,
    },
    {
      sourceField: 'requestAppointment.comment',
      commentType: DossierCommentsEnum.appointment,
      preferredStates: ['COMPLETE', 'CANCELLED'],
      value: legacyDossier?.requestAppointment?.comment,
    },
    {
      sourceField: 'claim.sinisterComment',
      commentType: DossierCommentsEnum.sinister,
      preferredStates: ['QUALIFIED', 'INIT', 'SLOT_SEARCH'],
      value: legacyDossier?.claim?.sinisterComment,
    },
    {
      sourceField: 'insurerData.comment',
      commentType: DossierCommentsEnum.insurer,
      preferredStates: ['QUALIFIED', 'INIT'],
      value: legacyDossier?.insurerData?.comment,
    },
    {
      sourceField: 'insurerData.abandonmentComment',
      commentType: DossierCommentsEnum.abandon,
      preferredStates: ['ABANDONED'],
      value: legacyDossier?.insurerData?.abandonmentComment,
    },
    {
      sourceField: 'insurerData.finishLaterComment',
      commentType: DossierCommentsEnum.finishLater,
      value: legacyDossier?.insurerData?.finishLaterComment,
    },
  ];

  return legacyConfigs
    .map((config) => buildLegacyComment(dossier, config))
    .filter((comment): comment is DossierComments => !!comment);
}

/** Normalizes collection entries and appends legacy comments not represented there yet. */
export function normalizeDossierComments(
  user?: { id: string; type: UserType; name?: string },
  dossier?: Partial<Dossier>
): DossierComments[] {
  const comments: DossierComments[] = [];
  const existingCommentKeys = new Set<string>();
  const existingTimelineEventKeys = new Set<string>();

  (dossier?.dossierComments || [])
    .map((comment) => sanitizeComment(comment, dossier, user))
    .filter((comment): comment is DossierComments => !!comment)
    .forEach((comment) => {
      const timelineEventKey = getTimelineEventDedupKey(comment);
      if (existingTimelineEventKeys.has(timelineEventKey)) {
        return;
      }
      existingTimelineEventKeys.add(timelineEventKey);
      existingCommentKeys.add(getDedupKey(comment));
      comments.push(comment);
    });

  extractLegacyDossierComments(dossier).forEach((comment) => {
    const key = getDedupKey(comment);
    if (existingCommentKeys.has(key)) {
      return;
    }
    existingCommentKeys.add(key);
    comments.push(comment);
  });

  return comments.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );
}

/** Returns a read-ready dossier with legacy comments exposed in the canonical collection. */
export function hydrateDossierComments<T extends Partial<Dossier>>(
  dossier: T
): T {
  if (!dossier) {
    return dossier;
  }

  dossier.dossierComments = normalizeDossierComments(undefined, dossier);
  let normalizedDossier = stripLegacyCommentFields(dossier);
  return normalizedDossier;
}

/** Returns the newest normalized comment among the requested types. */
export function getLatestDossierCommentByType(
  dossier: Partial<Dossier> | undefined,
  commentTypes: DossierCommentsType[],
  user?: { id: string; type: UserType; name?: string }
): DossierComments | undefined {
  const comments = normalizeDossierComments(user, dossier).filter((comment) =>
    commentTypes.includes(comment.commentType)
  );

  return comments[0];
}

/** Returns latest matching text for display-oriented consumers. */
export function getLatestDossierCommentTextByType(
  dossier: Partial<Dossier> | undefined,
  commentTypes: DossierCommentsType[],
  user?: { id: string; type: UserType; name?: string }
): string {
  return (
    getLatestDossierCommentByType(dossier, commentTypes, user)?.comment || ''
  );
}

/** Removes obsolete comment properties after their values have been normalized. */
export function stripLegacyCommentFields<T extends Partial<Dossier>>(
  dossier: T
): T {
  if (!dossier) {
    return dossier;
  }

  const omitFields = <V>(value: V, fields: string[]): V => {
    if (!value) {
      return value;
    }
    const cleanedValue = { ...value };
    fields.forEach((field) => delete cleanedValue[field]);
    return cleanedValue;
  };

  const cleanedDossier = {
    ...dossier,
    appointment: omitFields(dossier.appointment, ['comment']),
    requestAppointment: omitFields(dossier.requestAppointment, ['comment']),
    claim: omitFields(dossier.claim, ['sinisterComment']),
    insurerData: omitFields(dossier.insurerData, [
      'comment',
      'abandonmentComment',
      'finishLaterComment',
    ]),
  } as T;

  return cleanedDossier;
}
